import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ElementRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TimelineTask, TimeSlot, SlotDisplay } from '../../../../shared/models/activity.model';
import { MiniModalDetailTaskComponent } from './mini-modal-detail-task/mini-modal-detail-task';
import { GoogleCalendarEvent } from '../../../../shared/services/google-calendar.service';


type UiTask = TimelineTask & { title: string };

@Component({
  selector: 'app-activity-log',
  imports: [CommonModule, MiniModalDetailTaskComponent],
  templateUrl: './activity-log.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActivityLogComponent implements OnChanges {
  @Input() tasks: TimelineTask[] = [];
  @Input() slots: TimeSlot[] = [];
  @Input() zoom = 5;
  @Input() rowHeight = 32;
  @Input() calendarEvents: GoogleCalendarEvent[] = [];
  @Input() calendarLoading = false;
  /** Map of calendarId → hex color from Google Calendar */
  @Input() calendarColors: Map<string, string> = new Map();


  // ✅ utilisés dans ton HTML
  @Input() currentLineTop = -1;
  @Input() hoveredSlotIndex: number | null = null;

  @Output() hoverSlot = new EventEmitter<number | null>();

  @ViewChild('scrollContainer', { static: true })
  scrollContainer!: ElementRef<HTMLDivElement>;

  // key: slotIndex, value: list of tasks
  tasksBySlot = new Map<number, UiTask[]>();
  // key: slotIndex, value: list of calendar events
  calendarEventsBySlot = new Map<number, GoogleCalendarEvent[]>();
  // key: eventId, value: { startSlot, span } — precomputed once, O(1) lookups in template
  calendarEventMeta = new Map<string, { startSlot: number; span: number }>();

  get hasCalendar(): boolean {
    return this.calendarEvents.length > 0;
  }

  // Mini modal
  showMiniModal = false;
  selectedTasks: UiTask[] = [];
  modalPosition = { top: 0, left: 0 };

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tasks'] || changes['slots'] || changes['zoom']) {
      this.buildTasksBySlot();
    }
    if (changes['calendarEvents'] || changes['slots'] || changes['zoom']) {
      this.buildCalendarBySlot();
    }
  }


  private parseAnyDate(input: any): Date | null {
    if (!input) return null;
    const d = input instanceof Date ? input : new Date(input);
    return isNaN(d.getTime()) ? null : d;
  }

  private buildTasksBySlot(): void {
    this.tasksBySlot.clear();
    if (!this.slots?.length) return;

    const gridStartMs = this.slots[0].time.getTime();
    const zoomMs = this.zoom * 60_000;
    const gridEndMs = this.slots[this.slots.length - 1].time.getTime() + zoomMs;

    const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));

    for (const t of (this.tasks ?? [])) {
      const startDate = this.parseAnyDate((t as any).startTime);
      if (!startDate) continue;

      // ✅ endTime may be null/missing in Memetime data
      // If missing, assume it lasts exactly one zoom block (or 1 minute)
      const rawEnd = this.parseAnyDate((t as any).endTime);
      const fallbackEnd = new Date(startDate.getTime() + zoomMs);
      const endDate = rawEnd ?? fallbackEnd;

      // Guard: if end <= start, force minimal duration of 1 slot
      const safeEnd = endDate.getTime() > startDate.getTime()
        ? endDate
        : new Date(startDate.getTime() + zoomMs);

      // Clip to grid range
      const startMs = startDate.getTime();
      const endMs = safeEnd.getTime();

      if (endMs <= gridStartMs || startMs >= gridEndMs) {
        // completely outside the visible grid
        continue;
      }

      const clippedStart = clamp(startMs, gridStartMs, gridEndMs);
      const clippedEnd = clamp(endMs, gridStartMs, gridEndMs);

      // ✅ Convert to slot indices
      const startIndex = Math.floor((clippedStart - gridStartMs) / zoomMs);
      // Use ceil for end to ensure coverage of partial minutes
      const endIndexExclusive = Math.ceil((clippedEnd - gridStartMs) / zoomMs);

      // Build a meaningful title: prefer title, fallback to program name, or use type
      let displayTitle = '';
      const rawTitle = (t as any).title;
      const programName = (t as any).name;
      const taskType = (t as any).type;

      // Use title if it exists and is not empty/whitespace
      if (rawTitle && typeof rawTitle === 'string' && rawTitle.trim()) {
        displayTitle = rawTitle.trim();
      }
      // Fallback to program name (e.g., "chrome.exe" -> "Chrome")
      else if (programName && typeof programName === 'string' && programName.trim()) {
        // Clean up the program name: remove .exe/.app and capitalize
        displayTitle = programName
          .replace(/\.(exe|app)$/i, '')
          .replace(/[-_]/g, ' ')
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ');
      }
      // Last resort: use type or generic label
      else if (taskType && typeof taskType === 'string' && taskType.trim()) {
        displayTitle = taskType.charAt(0).toUpperCase() + taskType.slice(1);
      }
      else {
        displayTitle = 'Unknown Activity';
      }

      const uiTask: UiTask = {
        ...(t as any),
        title: displayTitle,
      };

      // ✅ Fill every slot covered by the interval
      for (let i = startIndex; i < endIndexExclusive; i++) {
        if (i < 0 || i >= this.slots.length) continue;
        const arr = this.tasksBySlot.get(i) ?? [];
        arr.push(uiTask);
        this.tasksBySlot.set(i, arr);
      }
    }

    // Sort each slot to keep stable ordering
    for (const [k, arr] of this.tasksBySlot.entries()) {
      arr.sort((a, b) => {
        const da = this.parseAnyDate((a as any).startTime);
        const db = this.parseAnyDate((b as any).startTime);
        return (da?.getTime() ?? 0) - (db?.getTime() ?? 0);
      });
      this.tasksBySlot.set(k, arr);
    }
  }

  private buildCalendarBySlot(): void {
    this.calendarEventsBySlot.clear();
    this.calendarEventMeta.clear();
    if (!this.slots?.length || !this.calendarEvents?.length) return;

    const gridStartMs = this.slots[0].time.getTime();
    const zoomMs = this.zoom * 60_000;
    const gridEndMs = this.slots[this.slots.length - 1].time.getTime() + zoomMs;

    for (const ev of this.calendarEvents) {
      const startMs = ev.start.dateTime
        ? new Date(ev.start.dateTime).getTime()
        : new Date(ev.start.date!).getTime();
      const endMs = ev.end.dateTime
        ? new Date(ev.end.dateTime).getTime()
        : new Date(ev.end.date!).getTime() + 86_400_000;

      if (endMs <= gridStartMs || startMs >= gridEndMs) continue;

      const clippedStart = Math.max(startMs, gridStartMs);
      const clippedEnd   = Math.min(endMs, gridEndMs);

      const startIndex       = Math.floor((clippedStart - gridStartMs) / zoomMs);
      const endIndexExclusive = Math.ceil((clippedEnd - gridStartMs) / zoomMs);
      const span = endIndexExclusive - startIndex;

      // Store O(1) metadata for template lookups
      this.calendarEventMeta.set(ev.id, { startSlot: startIndex, span });

      for (let i = startIndex; i < endIndexExclusive; i++) {
        if (i < 0 || i >= this.slots.length) continue;
        const arr = this.calendarEventsBySlot.get(i) ?? [];
        arr.push(ev);
        this.calendarEventsBySlot.set(i, arr);
      }
    }

    // Sort each slot's events by start time so column assignment is consistent across slots
    for (const [key, arr] of this.calendarEventsBySlot.entries()) {
      arr.sort((a, b) => {
        const ta = a.start.dateTime ? new Date(a.start.dateTime).getTime() : new Date(a.start.date!).getTime();
        const tb = b.start.dateTime ? new Date(b.start.dateTime).getTime() : new Date(b.start.date!).getTime();
        return ta - tb || a.id.localeCompare(b.id);
      });
      this.calendarEventsBySlot.set(key, arr);
    }
  }


  /** Returns absolute positioning (left %, width %) for an event within its slot's event list */
  getEventLayout(slotIndex: number, evId: string): { left: string; width: string } {
    const evs = this.calendarEventsBySlot.get(slotIndex) ?? [];
    const total = evs.length;
    if (total === 0) return { left: '0%', width: '100%' };
    const idx = evs.findIndex(e => e.id === evId);
    if (idx === -1)   return { left: '0%', width: '100%' };
    const pct = 100 / total;
    return { left: `${idx * pct}%`, width: `${pct}%` };
  }

  getCalendarEventsForSlot(slotIndex: number): GoogleCalendarEvent[] {
    return this.calendarEventsBySlot.get(slotIndex) ?? [];
  }

  /** Returns the calendar's hex color, with a sensible fallback */
  getEventColor(ev: GoogleCalendarEvent): string {
    return this.calendarColors.get(ev.calendarId ?? '') ?? '#6366f1'; // indigo fallback
  }

  /** Returns a very light tinted background (hex color at ~10% opacity in CSS) */
  getEventBg(ev: GoogleCalendarEvent): string {
    const hex = this.getEventColor(ev);
    return hex + '18'; // 18 = ~9% opacity in hex
  }

  /** O(1) — was O(n) scan of previous slot */
  isFirstSlotOfEvent(slotIndex: number, evId: string): boolean {
    return this.calendarEventMeta.get(evId)?.startSlot === slotIndex;
  }

  /** O(1) — was O(n) scan forward from slot */
  eventSlotSpan(_slotIndex: number, evId: string): number {
    return this.calendarEventMeta.get(evId)?.span ?? 1;
  }


  // ✅ ton HTML appelle: @let display = getSlotDisplay(slot.index);
  getSlotDisplay(slotIndex: number): SlotDisplay | null {
    const list = this.tasksBySlot.get(slotIndex) ?? [];
    if (list.length === 0) {
      return {
        hasActivity: false,
        mainTask: null as any,
        hiddenTasks: [],
      } as SlotDisplay;
    }

    // Afficher la 1ère tâche, et mettre les autres en "hiddenTasks"
    const [first, ...rest] = list;

    return {
      hasActivity: true,
      mainTask: first as any,
      hiddenTasks: rest as any,
    } as SlotDisplay;
  }

  // Clique sur un slot affiché : ouvrir mini modal avec main + hidden
  onTaskClick(event: MouseEvent, display: SlotDisplay): void {
    event.stopPropagation();

    const tasks: UiTask[] = [];
    if (display?.mainTask) tasks.push(display.mainTask as any);
    if (display?.hiddenTasks?.length) tasks.push(...(display.hiddenTasks as any));

    this.selectedTasks = tasks;

    // Position du mini-modal (proche du clic)
    const x = event.clientX;
    const y = event.clientY;

    // Clamp simple pour éviter de sortir de l’écran
    const margin = 12;
    const maxX = window.innerWidth - margin;
    const maxY = window.innerHeight - margin;

    this.modalPosition = {
      left: Math.min(Math.max(x, margin), maxX),
      top: Math.min(Math.max(y, margin), maxY),
    };

    this.showMiniModal = true;
  }
}
